import React, { useState, forwardRef, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Slide,
  TextField,
  IconButton,
  Typography,
  styled,
  Snackbar,
  Alert,
  Stack,
  Chip,
  Paper,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  useMediaQuery,
  DialogActions
} from "@mui/material";
import {
  Close,
  SupportAgent,
  CloudUpload,
  Send,
  AttachFile,
  CancelOutlined
} from "@mui/icons-material";
import { useTheme } from "@emotion/react";
import axios from "axios";
import { customerSupport } from "../../api/userApi";

// Transition animation for dialog
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Styled components to match the dark theme
const StyledTextFieldContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(2, 0),
  margin: theme.spacing(1, 0)
}));

const StyledInputLabel = styled("label")(({ theme }) => ({
  position: "absolute",
  top: "-10px",
  color: "#D1D5DB",
  fontWeight: "bold",
  fontSize: "14px",
  pointerEvents: "none"
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  input: {
    color: "#FCFCFC"
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      color: "#9CA3AF",
      borderColor: "#333333"
    },
    "&:hover fieldset": {
      borderColor: "#FCFCFC"
    },
    "&.Mui-focused fieldset": {
      color: "#9CA3AF",
      borderColor: "#FCFCFC"
    }
  },
  "& .MuiInputBase-multiline": {
    color: "#FCFCFC"
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  color: "#FCFCFC",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#333333"
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#FCFCFC"
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#FCFCFC"
  },
  "& .MuiSelect-icon": {
    color: "#9CA3AF"
  }
}));

const StyledUploadPaper = styled(Paper)(
  ({ theme, isDragActive, isOverSizeLimit }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderStyle: "dashed",
    cursor: "pointer",
    backgroundColor: isOverSizeLimit
      ? "rgba(220, 38, 38, 0.1)"
      : isDragActive
      ? "rgba(54, 138, 208, 0.1)"
      : "#1C1C1C",
    borderColor: isOverSizeLimit
      ? "#DC2626"
      : isDragActive
      ? "#368AD0"
      : "#333333",
    borderWidth: "2px",
    borderRadius: "12px",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "rgba(54, 138, 208, 0.05)",
      borderColor: "#368AD0"
    }
  })
);

const CustomerSupport = ({
  open,
  setOpen,
  setOpenSnackbar,
  setSnackbarMessage
}) => {
  // Form state
  const [issueType, setIssueType] = useState("");
  const [description, setDescription] = useState("");
  const [device, setDevice] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  useEffect(() => {
    setIssueType("");
    setDescription("");
    setAttachments([]);
  }, [open]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Issue types
  const issueTypes = [
    "Technical Problem",
    "Billing Inquiry",
    "Feature Request",
    "General Question / Other"
  ];

  // Handle dialog close
  const handleClose = () => {
    if (!loading) {
      setOpen(false);
      setAttachments([]);
      setIssueType("");
      setDescription("");
      setDevice("");
    }
  };

  // Handle file upload
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 10) {
      setSnackbar({
        open: true,
        message: "Maximum 10 files allowed",
        severity: "error"
      });
      return;
    }
    if (files.length > 0) {
      setAttachments((prevAttachments) => [...prevAttachments, ...files]);
    }
    // Clear input value to allow selecting the same file again
    event.target.value = null;
  };

  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      setAttachments((prevAttachments) => [...prevAttachments, ...files]);
    }
  };

  // Remove an attachment
  const handleRemoveAttachment = (index) => {
    setAttachments((prevAttachments) =>
      prevAttachments.filter((_, i) => i !== index)
    );
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Form validation
    if (!issueType || !description) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields",
        severity: "error"
      });
      return;
    }

    setLoading(true);

    // Create FormData for file upload
    const formData = new FormData();
    formData.append("issueType", issueType);
    formData.append("description", description);
    if (device) formData.append("device", device);
    // Append multiple files
    attachments.forEach((file) => {
      formData.append("images", file);
    });

    try {
      // Call your Node.js API endpoint
      const response = await customerSupport(formData);
      if (response?.message?.includes("success")) {
        setSnackbarMessage(
          "Your request has been submitted. Please allow 1-2 business days (M-F EST) for a response."
        );
        setOpenSnackbar(true);
      }
      // Close modal first
      // Reset form and show success message
      setIssueType("");
      setDescription("");
      setAttachments([]);
      setDevice("");
      setTimeout(() => {
        handleSnackbarClose();
        handleClose();
      }, 1000);
    } catch (error) {
      console.error("Error submitting support request:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.error ||
          "Failed to submit your request. Please try again later.",
        severity: "error"
      });
    } finally {
      setLoading(false);
      setTimeout(() => {
        setOpenSnackbar(false);
      }, 6000);
    }
  };

  // Calculate total size of attachments
  const totalAttachmentSize = attachments.reduce(
    (total, file) => total + file.size,
    0
  );
  const totalSizeInMB = (totalAttachmentSize / (1024 * 1024)).toFixed(2);
  const isOverSizeLimit = totalAttachmentSize > 20 * 1024 * 1024; // 20MB limit

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Dialog
      TransitionComponent={Transition}
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: "#1C1C1C",
          color: "#FCFCFC",
          width: isSmallScreen ? "95%" : "500px",
          margin: isSmallScreen ? "10px" : "auto",
          overflow: "visible", // Changed from "hidden"
          maxHeight: "84vh", // Add this
          position: "relative"
          // overflow: "hidden" // Prevents content from overflowing
        }
      }}
    >
      <Box
        sx={{
          overflow: "auto",
          zIndex: 1000000000,
          maxHeight: "84vh", // Changed from "100vh"
          display: "flex",
          flexDirection: "column",
          position: "relative"
        }}
      >
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "grey.500",
            zIndex: 1
          }}
        >
          <Close />
        </IconButton>

        <DialogContent
          sx={{
            flexGrow: 1,
            overflow: "auto",
            p: isSmallScreen ? 2 : 3,
            maxHeight: isSmallScreen ? "calc(70vh - 100px)" : "auto", // Adjusted calculation
            "&::-webkit-scrollbar": {
              width: "2px", // Make scrollbar visible but thin
              display: "block" // Changed from "none"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#333333",
              borderRadius: "4px"
            },
            "&::-webkit-scrollbar-track": {
              // Add this
              backgroundColor: "#1C1C1C"
            }
          }}
        >
          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            sx={{
              zIndex: 160000 // Higher than dialog
            }}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>

          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            mt={1}
            mb={3}
          >
            <SupportAgent sx={{ fontSize: 48, color: "#368AD0", mb: 1 }} />
            <Typography variant="h5" fontWeight="600" textAlign="center">
              How can we help?
            </Typography>
            <Typography
              variant="body2"
              color="#FCFCFC"
              textAlign="center"
              my={1.5}
            >
              Please allow 1-2 business days (M-F EST) {isMobile && <br />} for
              a response.
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" gap={1} mt={1}>
            {/* Issue Type */}
            <StyledTextFieldContainer>
              <StyledInputLabel htmlFor="issueType">
                Issue Type *
              </StyledInputLabel>
              <FormControl fullWidth variant="outlined">
                <StyledSelect
                  value={issueType}
                  onChange={(e) => setIssueType(e.target.value)}
                  disabled={loading}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Typography sx={{ color: "#9CA3AF" }}>
                          Select an issue type
                        </Typography>
                      );
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#1C1C1C",
                        color: "#FCFCFC",
                        "& .MuiMenuItem-root": {
                          "&:hover": {
                            bgcolor: "rgba(54, 138, 208, 0.1)"
                          },
                          "&.Mui-selected": {
                            bgcolor: "rgba(54, 138, 208, 0.2)",
                            "&:hover": {
                              bgcolor: "rgba(54, 138, 208, 0.3)"
                            }
                          }
                        }
                      }
                    }
                  }}
                >
                  {issueTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </StyledTextFieldContainer>
            <StyledTextFieldContainer>
              <StyledInputLabel htmlFor="issueType">
                Browser & Device
              </StyledInputLabel>
              <StyledTextField
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "14px"
                  }
                }}
                // multiline
                // rows={isSmallScreen ? 3 : 4}
                placeholder="(for e.g. Safari / iPhone 16, Chrome / Samsung Galaxy S23)"
                value={device}
                onChange={(e) => setDevice(e.target.value)}
                disabled={loading}
                variant="outlined"
              />
            </StyledTextFieldContainer>

            {/* Description */}
            <StyledTextFieldContainer>
              <StyledInputLabel htmlFor="description">
                Description *
              </StyledInputLabel>
              <StyledTextField
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "14px"
                  }
                }}
                fullWidth
                multiline
                rows={isSmallScreen ? 3 : 4}
                placeholder="Please describe your issue in detail..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={loading}
                variant="outlined"
              />
            </StyledTextFieldContainer>

            {/* File Upload */}
            <StyledUploadPaper
              isDragActive={isDragActive}
              isOverSizeLimit={isOverSizeLimit}
              onClick={() => document.getElementById("file-upload").click()}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                id="file-upload"
                type="file"
                hidden
                multiple
                onChange={handleFileChange}
                disabled={loading}
                accept="image/*"
              />
              <CloudUpload sx={{ fontSize: 32, color: "#368AD0", mb: 1 }} />
              <Typography
                variant={isSmallScreen ? "body2" : "body1"}
                align="center"
                color="#FCFCFC"
              >
                {isDragActive
                  ? "Drop files here"
                  : "Drag & drop files or click to browse"}
              </Typography>
              <Typography
                variant="caption"
                color="#D1D5DB"
                align="center"
                sx={{ mt: 0.5 }}
              >
                Max total size: 20MB (Max files: 10)
              </Typography>
              {isOverSizeLimit && (
                <Typography
                  variant="caption"
                  color="#DC2626"
                  align="center"
                  sx={{ mt: 1 }}
                >
                  Total size exceeds 20MB limit. Please remove some files.
                </Typography>
              )}
            </StyledUploadPaper>

            {/* Display Selected Files */}
            {attachments.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }} color="#D1D5DB">
                  Attachments ({totalSizeInMB}MB total):
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {attachments.map((file, index) => (
                    <Chip
                      key={index}
                      icon={<AttachFile sx={{ color: "#368AD0" }} />}
                      label={`${
                        file.name.length > (isSmallScreen ? 10 : 15)
                          ? file.name.substring(0, isSmallScreen ? 7 : 12) +
                            "..."
                          : file.name
                      } `}
                      onDelete={() => handleRemoveAttachment(index)}
                      deleteIcon={
                        <CancelOutlined sx={{ color: "#9CA3AF !important" }} />
                      }
                      disabled={loading}
                      sx={{
                        mb: 1,
                        color: "#FCFCFC",
                        bgcolor: "#2A2A2A",
                        borderColor: "#333333",
                        "& .MuiChip-deleteIcon": {
                          color: "#9CA3AF",
                          "&:hover": {
                            color: "#FCFCFC"
                          }
                        }
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: isSmallScreen ? 2 : 3,
            pt: 1,
            pb: isSmallScreen ? 3 : 3,
            bgcolor: "#1C1C1C",
            borderTop: "1px solid #333333",
            justifyContent: "center"
          }}
        >
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={!issueType || !description || loading || isOverSizeLimit}
            endIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Send />
              )
            }
            sx={{
              bgcolor: "#368AD0",
              color: "white",
              py: 1.5,
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              "&:hover": {
                bgcolor: "#2a6ba4"
              },
              ":disabled": {
                backgroundColor: "#A7C8E9",
                color: "#64AEDD",
                pointerEvents: "none"
              }
            }}
          >
            {loading ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CustomerSupport;
